/**
 * Retail catalogue dual-description helpers.
 * Original = price-list / staff original; AI = generated; selected source is explicit.
 */

export type CatalogueDescriptionSource = "original" | "ai";

export type CatalogueDescriptionFields = {
  description?: string | null;
  originalDescription?: string | null;
  aiGeneratedDescription?: string | null;
  selectedDescriptionSource?: string | null;
  descriptionSource?: string | null;
  descriptionStatus?: string | null;
};

export function normaliseCatalogueDescriptionSource(
  value: unknown,
): CatalogueDescriptionSource {
  return value === "ai" ? "ai" : "original";
}

export function hasCatalogueAiDescription(row: CatalogueDescriptionFields): boolean {
  return String(row.aiGeneratedDescription ?? "").trim().length > 0;
}

export function resolveEffectiveCatalogueDescription(row: CatalogueDescriptionFields): string {
  const source = normaliseCatalogueDescriptionSource(row.selectedDescriptionSource);
  if (source === "ai") {
    const ai = String(row.aiGeneratedDescription ?? "").trim();
    if (ai) return ai;
  }
  const original = String(row.originalDescription ?? "").trim();
  if (original) return original;
  // Legacy fallback while migrating older clients/readers.
  return String(row.description ?? "").trim();
}

export function buildSyncedDescriptionMeta(row: {
  originalDescription?: string | null;
  aiGeneratedDescription?: string | null;
  selectedDescriptionSource: CatalogueDescriptionSource;
}): {
  description: string | null;
  descriptionSource: "file" | "ai" | "manual" | null;
  descriptionStatus: "pending" | "generated" | "failed" | "manual";
  enrichmentStatus: "pending" | "partial" | "complete";
} {
  const effective = resolveEffectiveCatalogueDescription(row);
  if (!effective) {
    return {
      description: null,
      descriptionSource: null,
      descriptionStatus: "pending",
      enrichmentStatus: "pending",
    };
  }
  const source = normaliseCatalogueDescriptionSource(row.selectedDescriptionSource);
  if (source === "ai" && hasCatalogueAiDescription(row)) {
    return {
      description: effective,
      descriptionSource: "ai",
      descriptionStatus: "generated",
      enrichmentStatus: "complete",
    };
  }
  return {
    description: effective,
    descriptionSource: "file",
    descriptionStatus: "manual",
    enrichmentStatus: "complete",
  };
}
